import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { clearAuthSession, getAccessToken, getRefreshToken, setTokens } from './session';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://banking-dev.devopsark.com/api/v1';
export const EMPLOYEE_TOKEN_KEY = 'nova_employee_token';
const DEVICE_ID_KEY = 'nova_device_id';
const REFRESH_PATH = '/auth/refresh';

export interface FieldError {
  field: string;
  message: string;
}

export interface ApiEnvelope<T> {
  success?: boolean;
  message?: string;
  data?: T;
  errorCode?: string;
  fieldErrors?: FieldError[];
  correlationId?: string;
  timestamp?: string | number;
}

export class ApiError extends Error {
  code: string;
  status?: number;
  correlationId?: string;
  fieldErrors: FieldError[];

  constructor(
    code: string,
    message: string,
    status?: number,
    correlationId?: string,
    fieldErrors: FieldError[] = []
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.correlationId = correlationId;
    this.fieldErrors = fieldErrors;
  }
}

export const getDeviceId = (): string => {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

const getDeviceInfo = () => ({
  channel: 'WEB',
  deviceId: getDeviceId(),
  deviceName: `${navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Browser'} on ${navigator.platform || 'Web'}`,
  operatingSystem: navigator.platform || 'Web',
  osVersion: 'unknown',
  appVersion: import.meta.env.VITE_APP_VERSION || 'web',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  locale: navigator.language,
  userAgent: navigator.userAgent,
});

export const deviceInfo = getDeviceInfo;

export const isRealApiEnabled = (): boolean => import.meta.env.VITE_USE_REAL_API === 'true';

export const toPaise = (rupees: number): number => Math.round(rupees * 100);
export const fromPaise = (paise: number): number => paise / 100;
export const createIdempotencyKey = (): string => crypto.randomUUID();

const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

let refreshPromise: Promise<string | null> | null = null;

const clearSessionAndRedirect = (): void => {
  clearAuthSession();
  if (window.location.pathname !== '/welcome') window.location.assign('/welcome');
};

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const response = await axios.post<ApiEnvelope<{ accessToken?: string; refreshToken?: string }>>(
      `${API_BASE_URL}${REFRESH_PATH}`,
      { refreshToken, deviceInfo: getDeviceInfo() },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
    const data = unwrap(response.data, 'Session refresh failed.');
    if (!data.accessToken) return null;
    setTokens(data.accessToken, data.refreshToken);
    return data.accessToken;
  } catch {
    return null;
  }
};

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const url = config.url ?? '';
  const employeeToken = localStorage.getItem(EMPLOYEE_TOKEN_KEY);
  const accessToken = getAccessToken();
  const token = url.includes('/onboarding/review') ? employeeToken : accessToken;
  if (token && !url.endsWith(REFRESH_PATH)) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const isPinVerification = config?.url?.includes('/auth/mpin/verify') || config?.url?.includes('/upi/pin/verify');
    // A 401 from signing in (wrong password or code) is an answer to show, not an expired
    // session: don't try to refresh, and don't reload the page to Welcome.
    const isSignInStep = ['/auth/login', '/auth/password/', '/auth/username/'].some((path) => config?.url?.includes(path));
    if (error.response?.status !== 401 || !config || config._retry || config.url?.endsWith(REFRESH_PATH) || isPinVerification || isSignInStep) {
      return Promise.reject(error);
    }

    config._retry = true;
    refreshPromise ??= refreshAccessToken().finally(() => { refreshPromise = null; });
    const token = await refreshPromise;
    if (!token) {
      clearSessionAndRedirect();
      return Promise.reject(error);
    }
    config.headers.Authorization = `Bearer ${token}`;
    return client.request(config);
  }
);

const toApiError = (error: unknown, fallback: string): ApiError => {
  const axiosError = error as AxiosError<ApiEnvelope<unknown>>;
  const status = axiosError.response?.status;
  const body = axiosError.response?.data;
  if (body && typeof body === 'object') {
    const code = body.errorCode || (status ? `HTTP_${status}` : 'ERROR');
    const message = code === 'AUTH_016' ? 'Too many attempts, try again shortly' : (body.message || fallback);
    return new ApiError(
      code,
      message,
      status,
      body.correlationId,
      body.fieldErrors || []
    );
  }
  if (!axiosError.response) return new ApiError('NETWORK', 'Could not reach the server. Check your connection and try again.');
  return new ApiError(status ? `HTTP_${status}` : 'ERROR', fallback, status);
};

export const unwrap = <T>(envelope: ApiEnvelope<T>, fallback: string): T => {
  if (envelope && envelope.success === false) {
    throw new ApiError(envelope.errorCode || 'ERROR', envelope.message || fallback, undefined, envelope.correlationId, envelope.fieldErrors || []);
  }
  return (envelope?.data ?? (envelope as unknown as T)) as T;
};

export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await client.get<ApiEnvelope<T>>(url, config);
    return unwrap(response.data, 'Request failed. Please try again.');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toApiError(error, 'Request failed. Please try again.');
  }
};

export const post = async <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await client.post<ApiEnvelope<T>>(url, body, config);
    return unwrap(response.data, 'Request failed. Please try again.');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toApiError(error, 'Request failed. Please try again.');
  }
};

export const put = async <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await client.put<ApiEnvelope<T>>(url, body, config);
    return unwrap(response.data, 'Request failed. Please try again.');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toApiError(error, 'Request failed. Please try again.');
  }
};

export const patch = async <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await client.patch<ApiEnvelope<T>>(url, body, config);
    return unwrap(response.data, 'Request failed. Please try again.');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toApiError(error, 'Request failed. Please try again.');
  }
};

export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await client.delete<ApiEnvelope<T>>(url, config);
    return unwrap(response.data, 'Request failed. Please try again.');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toApiError(error, 'Request failed. Please try again.');
  }
};

export const getBlob = async (url: string, config?: AxiosRequestConfig): Promise<Blob> => {
  try {
    const response = await client.get<Blob>(url, { ...config, responseType: 'blob' });
    return response.data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toApiError(error, 'Could not download the file.');
  }
};

export default client;
